/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "onChange", "validLanguages", "isPublic", "optionsFilter", "useLanguageSetting", "useBetaLanguages", "betaLocales", "betaPublicLocales"];
import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import LanguageNames from '../constants/LanguageNames';
import findLanguage from '../internal/utils/findLanguage';
import { overrideTextTo } from '../internal/constants/LanguageOverrides';
import sortOptions from '../internal/utils/sortOptions';
import * as BetaLocales from '../internal/constants/BetaLocales';
import { Select } from 'foundations-components/transitional/components/Select';
import { jsx as _jsx } from "react/jsx-runtime";
const getLanguageOptions = ({
  languages,
  validLanguages,
  optionsFilter,
  useLanguageSetting
}) => {
  const languageOptions = [];
  const languageKeys = Object.keys(languages);
  if (!languageKeys.length) {
    return languageOptions;
  }
  const betaBadge = {
    use: 'beta',
    text: I18n.text('i18nAddon.languagesBeta')
  };
  const addToLanguageOptions = (languageData, displayText) => {
    languageOptions.push({
      badge: !useLanguageSetting && languageData.beta ? betaBadge : null,
      optionsFilter: PropTypes.func,
      text: displayText,
      value: languageData.language
    });
  };
  const overrideTextAndAddLanguage = languageData => {
    const language = languageData.language;
    const validLanguageKey = overrideTextTo[language] || language;
    const displayText = useLanguageSetting ? I18n.text(`i18nAddon.languages.${validLanguageKey}`) : LanguageNames[validLanguageKey];
    addToLanguageOptions(languageData, displayText);
  };
  if (validLanguages) {
    validLanguages.forEach(lang => {
      const languageData = languages[lang];
      if (languageData) {
        overrideTextAndAddLanguage(languageData);
      }
    });
  } else {
    languageKeys.forEach(lang => {
      const languageData = languages[lang];
      overrideTextAndAddLanguage(languageData);
    });
  }
  if (optionsFilter) {
    return languageOptions.filter(optionsFilter);
  }
  return languageOptions;
};
class HubSpotLanguageSelect extends Component {
  constructor(...args) {
    super(...args);
    this.handleLanguageChange = (values, options, onChange, multi) => {
      const selectedLang = findLanguage(options, values, multi);
      return onChange ? onChange(selectedLang) : selectedLang;
    };
    this.guessLanguageProps = (options, props) => {
      if (!props.value && !props.defaultValue) {
        return props;
      }
      const validProps = Object.assign({}, props);
      const bestGuess = {
        value: null,
        defaultValue: null
      };
      const checkRegexMatch = (localeRegex, value) => {
        if (typeof value === 'string') {
          return value.match(localeRegex);
        }
        let allMatch = true;
        value.forEach(val => {
          if (!val.match(localeRegex)) {
            allMatch = false;
          }
        });
        return allMatch;
      };
      const localeRegex = /^\w{2}(-\w*)?$/g;
      const isValidValue = props.value && checkRegexMatch(localeRegex, props.value);
      const isValidDefaultValue = props.defaultValue && checkRegexMatch(localeRegex, props.defaultValue);
      const valueObject = {
        value: {},
        defaultValue: {}
      };
      const findMatches = (type, optionValue) => {
        const propValue = props[type];
        if (typeof propValue === 'string') {
          if (optionValue === propValue) {
            bestGuess[type] = optionValue;
          } else if (!bestGuess[type] && propValue.startsWith(optionValue)) {
            bestGuess[type] = optionValue;
          }
        } else {
          propValue.forEach(value => {
            if (optionValue === value) {
              valueObject[type][value] = optionValue;
            } else if (!valueObject[value] && value.startsWith(optionValue)) {
              valueObject[type][value] = optionValue;
            }
          });
        }
      };
      options.forEach(option => {
        if (isValidValue) {
          findMatches('value', option.value);
        }
        if (isValidDefaultValue) {
          findMatches('defaultValue', option.value);
        }
      });
      const addToProps = type => {
        const val = [];
        Object.values(valueObject[type]).forEach(value => {
          val.push(value);
        });
        validProps[type] = val;
      };
      if (bestGuess.value) {
        validProps.value = bestGuess.value;
      } else if (isValidValue && props.value) {
        addToProps('value');
      }
      if (bestGuess.defaultValue) {
        validProps.defaultValue = bestGuess.defaultValue;
      } else if (isValidDefaultValue && props.defaultValue) {
        addToProps('defaultValue');
      }
      return validProps;
    };
  }
  render() {
    const _this$props = this.props,
      {
        className,
        onChange,
        validLanguages,
        isPublic,
        optionsFilter,
        useLanguageSetting,
        useBetaLanguages,
        betaLocales,
        betaPublicLocales
      } = _this$props,
      passThroughProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    let options = isPublic ? Object.assign({}, I18n.baseLocales, I18n.publicLocales) : Object.assign({}, I18n.baseLocales);
    if (useBetaLanguages) {
      const betaLocalesToUse = isPublic ? betaPublicLocales : betaLocales;
      options = Object.assign({}, options, betaLocalesToUse);
    }
    const languageOptions = getLanguageOptions({
      languages: options,
      validLanguages,
      optionsFilter,
      useLanguageSetting
    }).sort(sortOptions);
    const validProps = this.guessLanguageProps(languageOptions, passThroughProps);
    if (className) {
      return /*#__PURE__*/_jsx(UISelect, Object.assign({
        className: className
      }, validProps, {
        onChange: evt => this.handleLanguageChange(evt.target.value, options, onChange, validProps.multi),
        options: languageOptions
      }));
    }
    return /*#__PURE__*/_jsx(Select, Object.assign({}, validProps, {
      onChange: evt => this.handleLanguageChange(evt.target.value, options, onChange, validProps.multi),
      options: languageOptions
    }));
  }
}
HubSpotLanguageSelect.propTypes = {
  isPublic: PropTypes.bool,
  useBetaLanguages: PropTypes.bool,
  useLanguageSetting: PropTypes.bool,
  validLanguages: PropTypes.array
};
HubSpotLanguageSelect.defaultProps = {
  isPublic: false,
  useBetaLanguages: false,
  useLanguageSetting: false,
  betaLocales: BetaLocales.betaLocales,
  betaPublicLocales: BetaLocales.betaPublicLocales
};
export default HubSpotLanguageSelect;